import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import Bugsnag from '@bugsnag/js';
import { PartnerTagComponent } from '@components/atoms/partner-tag/partner-tag.component';
import { TagComponent } from '@components/atoms/tag/tag.component';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import type { AlgoliaClient } from '@models/client.model';
import { Store } from '@ngrx/store';
import { ClientTagPipe } from '@pipes/get-tag-from-algolia-hit.pipe';
import { AlgoliaService } from '@services/algolia.service';
import { BankingPartnerStore } from '@stores/partner.store';
import { selectCurrentCountry } from '@stores/router.store';
import { WzbWaitingSpinnerComponent } from '@wizbii-components/angular-ui';
import { BankingPartnerEnum } from '@wizbii/utils/models';
import { derivedAsync } from 'ngxtension/derived-async';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'hlp-global-searchbar',
  imports: [
    MatFormFieldModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    TagComponent,
    WzbWaitingSpinnerComponent,
    ClientTagPipe,
    MatTooltip,
    PartnerTagComponent,
  ],
  template: `
    <div class="tw:flex tw:items-center tw:bg-white tw:text-primary tw:rounded-[10px]">
      <input
        class="tw:grow tw:px-3 tw:py-3.5 tw:rounded-[10px]"
        [matAutocomplete]="auto"
        [formControl]="searchCtrl"
        matInput
        aria-label="Rechercher un client"
        placeholder="Rechercher un client"
      />
      <mat-icon class="tw:m-3" svgIcon="search" />
      <mat-autocomplete
        [displayWith]="displayFn"
        (optionSelected)="selectClient($event.option.value)"
        #auto="matAutocomplete"
      >
        @if (!isLoading()) {
          @for (hit of algoliaSearch(); track hit._id) {
            <mat-option
              class="tw:flex tw:p-3!"
              [value]="hit"
              [matTooltip]="hit.fullName.length > 34 ? hit.fullName : undefined"
              matTooltipShowDelay="800"
              matTooltipPosition="above"
            >
              <div class="tw:flex tw:items-center tw:justify-between">
                <div>
                  <div class="tw:flex tw:items-center tw:gap-2">
                    <span class="tw:inline-block tw:max-w-xs tw:truncate!">{{ hit.fullName }}</span>

                    @if (hit.partnerId === BankingPartner.Swan) {
                      <hlp-tag [innerClass]="hit.partnerId">
                        <mat-icon class="tag-swan" svgIcon="swan" />
                      </hlp-tag>
                    } @else {
                      <hlp-partner-tag [partnerId]="hit.partnerId" />
                    }
                  </div>
                  <div class="tw:pt-2 tw:text-xs">
                    @if (hit.phoneNumber) {
                      <b>{{ hit.phoneNumber }}</b> | {{ hit.email }}
                    } @else {
                      {{ hit.email }}
                    }
                  </div>
                </div>
                @let tag = hit | clientTag;

                @if (tag) {
                  <hlp-tag class="tw:flex" [innerClass]="tag.color" [label]="tag.label" [icon]="tag.icon" />
                }
              </div>
            </mat-option>
          } @empty {
            @if (searchCtrl.value) {
              <mat-option class="tw:p-3!" disabled> Aucun résultat trouvé</mat-option>
            }
          }
        } @else {
          <mat-option class="tw:p-3! tw:w-full tw:mx-auto tw:text-primary tw:border tw:border-2" disabled>
            <div class="tw:mx-auto tw:flex tw:h-8 tw:w-fit tw:items-center tw:justify-center">
              <wzb-waiting-spinner class="tw:mx-auto tw:flex tw:w-fit" color="var(--color-secondary-2)" />
            </div>
          </mat-option>
        }
      </mat-autocomplete>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalSearchbarComponent {
  readonly #algoliaService = inject(AlgoliaService);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly bankingPartnerStore = inject(BankingPartnerStore);

  readonly currentCountry = this.#store.selectSignal(selectCurrentCountry);

  readonly BankingPartner = BankingPartnerEnum;
  readonly searchCtrl = new FormControl();

  readonly isLoading = signal(false);
  readonly algoliaIndex = derivedAsync(() =>
    this.#algoliaService.getIndex(environment.algolia.index.client).pipe(
      catchError((err) => {
        Bugsnag.notify(err);
        throw err;
      })
    )
  );

  readonly algoliaSearch = derivedAsync(() => {
    return this.searchCtrl.valueChanges.pipe(
      tap(() => {
        this.isLoading.set(true);
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(async (query) => {
        if (!query || typeof query !== 'string') return [];

        const algoliaRes = await this.algoliaIndex()
          ?.search<AlgoliaClient>('', {
            query,
            page: 0,
            cacheable: false,
            facetFilters: [`country:${this.currentCountry()}`],
            disableTypoToleranceOnAttributes: ['phoneNumber'],
          })
          .catch((err) => {
            Bugsnag.notify(err);
            return null;
          });

        return algoliaRes?.hits || [];
      }),
      tap(() => {
        this.isLoading.set(false);
      })
    );
  });

  selectClient(client: AlgoliaClient): void {
    this.#router.navigate(['/', this.currentCountry(), FeaturesRoutingEnum.Clients, client.userId]);
    this.searchCtrl.reset();
  }

  displayFn(client: AlgoliaClient): string {
    return client && client.fullName ? client.fullName : '';
  }
}
